<template>
  <div>
    <adn-quantity-input
      v-if="!isQuantityMaxedOutInCart"
      v-model="currentQuantity"
      :min="quantityMin"
      :max="nonNegativeQuantityMax"
      :step="quantityStep"
      :multiplier="multiplier"
      :unit="displayUnit"
      :show-tooltip="showTooltip"
      :tooltip-message="tooltipMessage"
      data-test="quantity-input"
      :disabled="isDisabled"
      @options-extended="onQuantityInputExpanded"
    ></adn-quantity-input>
    <template v-if="isMountedInCheckout && !isQuantityMaxedOutInCart && shouldShowEquivalentMeasurement">
      <p class="offer-quantity__equivalent-measurement">
        {{ i18n._('Soit') }}
        <span data-test="display-quantity">{{ displayQuantity }}</span>
      </p>
    </template>
    <p
      v-if="isMountedInCheckout && isShownMinQuantityMessage"
      class="offer-quantity__restrict-message"
      data-test="min-quantity-message"
    >
      {{
        i18n._('Le prix indiqué sʼapplique sur une quantité minimale de { minQuantityInUnit }.', {
          minQuantityInUnit: minQuantityInUnit,
        })
      }}
    </p>
    <p
      v-if="isMountedInCheckout && isShownMaxQuantityMessage"
      class="offer-quantity__restrict-message"
      data-test="max-quantity-message"
    >
      {{
        i18n._('Le prix indiqué ne sʼapplique pas au-delà de { maxQuantityInUnit }.', {
          maxQuantityInUnit: maxQuantityInUnit,
        })
      }}
    </p>
    <p
      v-if="isMountedInCheckout && isQuantityMaxedOutInCart"
      class="offer-quantity__restrict-message"
      data-test="quantity-maxed-out-in-cart"
    >
      {{ i18n._('La quantité maximale commandable est atteinte dans votre panier.') }}
    </p>
  </div>
</template>

<script lang="ts">
import Big from 'big.js'
import { defineComponent, computed, watch, ref } from 'vue'
import AdnQuantityInput from '@b2ag/design-system/src/components/AdnQuantityInput/AdnQuantityInput.vue'
import { formatVariableUnit } from '@b2ag/price/src/helpers/unit'
import { frenchPlural, formatNumber, pluralize } from '@b2ag/l10n'
import { i18n } from '@b2ag/locale'

const unitWithAgreement = frenchPlural('unité')
const doseWithAgreement = frenchPlural('dose')

export default defineComponent({
  name: 'POfferQuantity',
  components: { AdnQuantityInput },
  props: {
    value: {
      type: Number,
      default: 0,
    },
    modelValue: {
      type: Number,
      default: 0,
    },
    quantityMin: {
      type: Number,
      default: 0,
    },
    quantityMax: {
      type: Number,
      default: Infinity,
    },
    quantityStep: Number,
    measureUnit: {
      type: String,
      default: '',
    },
    measureQuantity: {
      type: Number,
      default: 0,
    },
    isSoldAsUnit: {
      type: Boolean,
      default: false,
    },
    isQuantityMaxedOutInCart: Boolean,
    isMountedInCheckout: {
      type: Boolean,
      default: false,
    },
    packaging: {
      type: String,
      default: '',
    },
    kind: String,
    isDisabled: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipMessage: String,
  },
  emits: ['input', 'quantityInputExpanded', 'update:modelValue'],
  setup(props, context) {
    const currentQuantity = ref(props.value || props.modelValue || props.quantityMin)

    const displayUnit = computed(() => {
      if (props.kind === 'semences') return formatVariableUnit(props.packaging)
      if (props.isSoldAsUnit) return props.measureUnit === 'MG' ? doseWithAgreement : unitWithAgreement
      return formatVariableUnit(props.measureUnit)
    })

    function computeQuantityInUnit(n = 0) {
      if (props.isSoldAsUnit) {
        return `${formatNumber(n)}\xa0${pluralize(displayUnit.value, n)}`
      }

      const q = +Big(props.measureQuantity).times(n)
      return `${formatNumber(q)}\xa0${pluralize(formatVariableUnit(props.measureUnit), q)}`
    }

    const shouldShowEquivalentMeasurement = computed(() => {
      return props.isSoldAsUnit && props.measureUnit !== 'unit' && !props.isQuantityMaxedOutInCart
    })
    const multiplier = computed(() => {
      return props.isSoldAsUnit || props.kind === 'semences' ? 1 : props.measureQuantity
    })
    const displayQuantity = computed(() => {
      const q = +Big(props.measureQuantity).times(currentQuantity.value)
      return `${formatNumber(q)}\xa0${pluralize(formatVariableUnit(props.measureUnit), q)}`
    })
    const minQuantityInUnit = computed(() => {
      return computeQuantityInUnit(props.quantityMin)
    })
    const maxQuantityInUnit = computed(() => {
      return computeQuantityInUnit(props.quantityMax)
    })
    const isShownMinQuantityMessage = computed(() => {
      return props.quantityMin > 1 && currentQuantity.value <= props.quantityMin && !props.isQuantityMaxedOutInCart
    })
    const isShownMaxQuantityMessage = computed(() => {
      if (!props.quantityMax) {
        return false
      }
      const isGreaterThanMax = props.quantityStep
        ? currentQuantity.value + props.quantityStep > props.quantityMax
        : currentQuantity.value >= props.quantityMax
      return isGreaterThanMax && !props.isQuantityMaxedOutInCart
    })
    const nonNegativeQuantityMax = computed(() => {
      return props.quantityMax < 0 ? 0 : props.quantityMax
    })

    function onQuantityInputExpanded(expandQuantityCount) {
      context.emit('quantityInputExpanded', expandQuantityCount)
    }

    watch(currentQuantity, (newVal) => {
      context.emit('input', newVal)
      context.emit('update:modelValue', newVal)
    })

    watch(
      () => props.value,
      (newVal) => {
        currentQuantity.value = newVal
      },
    )

    watch(
      () => props.modelValue,
      (newVal) => {
        currentQuantity.value = newVal
      },
    )

    return {
      currentQuantity,
      displayQuantity,
      displayUnit,
      isShownMaxQuantityMessage,
      isShownMinQuantityMessage,
      maxQuantityInUnit,
      minQuantityInUnit,
      multiplier,
      nonNegativeQuantityMax,
      shouldShowEquivalentMeasurement,
      onQuantityInputExpanded,
      i18n,
    }
  },
})
</script>
<style lang="scss" scoped>
.offer-quantity__restrict-message {
  margin-top: 0.6rem;
  color: var(--color-inactive-dark);
}

.offer-quantity__equivalent-measurement {
  margin-top: var(--spacing-2);

  @media (min-width: $breakpoint-xl) {
    display: inline-block;
    margin-left: 5px;
  }
}

.open-modal {
  pointer-events: none;
}
</style>
